import { useEffect, useState } from 'react'
import { Building2, ChevronDown, Plus, Check } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { useCompanyStore } from '@/store/company-store'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'

interface Company {
  id: string
  name: string
  invoice_prefix: string
  gst_number: string | null
  state_code: string | null
}

export function CompanySwitcher() {
  const { user } = useAuth()
  const { activeCompany, setActiveCompany } = useCompanyStore()
  const [companies, setCompanies] = useState<Company[]>([])
  const navigate = useNavigate()

  useEffect(() => {
    if (!user) return
    supabase
      .from('companies')
      .select('id, name, invoice_prefix, gst_number, state_code')
      .is('deleted_at', null)
      .eq('is_active', true)
      .then(({ data }) => {
        if (data) setCompanies(data)
      })
  }, [user])

  // Auto-select: no selection yet (fresh invite/login) OR the persisted
  // selection isn't in this user's list (stale/other-user leftovers) →
  // switch to the first company they can access.
  useEffect(() => {
    if (companies.length === 0) return
    const selectionValid = activeCompany && companies.some(c => c.id === activeCompany.id)
    if (!selectionValid) {
      setActiveCompany(companies[0])
    }
  }, [companies, activeCompany, setActiveCompany])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 px-2 max-w-xs">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary/10">
            <Building2 className="h-3.5 w-3.5 text-primary" />
          </div>
          <span className="text-sm font-medium truncate max-w-[140px]">
            {activeCompany?.name ?? 'Select Company'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Your Companies</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {companies.map((company) => (
          <DropdownMenuItem
            key={company.id}
            onClick={() => setActiveCompany(company)}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted text-xs font-semibold">
                {company.name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium">{company.name}</p>
                {company.gst_number && (
                  <p className="text-xs text-muted-foreground">{company.gst_number}</p>
                )}
              </div>
            </div>
            {activeCompany?.id === company.id && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
        {companies.length === 0 && (
          <DropdownMenuItem disabled>No companies yet</DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/settings/company/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Add Company
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
